import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, Upload, X, ChevronRight, ChevronLeft } from 'lucide-react';
import OctopusPrerequisSteps from '@/components/OctopusPrerequisSteps';

const API = process.env.REACT_APP_BACKEND_URL;

// Vérifie la structure d'un NIR (15 caractères, chiffres sauf positions 6-7 qui
// peuvent être "2A"/"2B" pour les personnes nées en Corse).
const isValidNirStructure = (raw) => {
  const cleaned = (raw || '').replace(/\s+/g, '').toUpperCase();
  if (cleaned.length !== 15) return false;
  const deptPart = cleaned.slice(5, 7);
  const rest = cleaned.slice(0, 5) + cleaned.slice(7);
  if (!/^\d+$/.test(rest)) return false;
  return /^\d{2}$/.test(deptPart) || deptPart === '2A' || deptPart === '2B';
};

const STATUT_LABELS = {
  VRP: 'Commercial VRP',
  VDI: 'Commercial VDI',
  AE: "Apporteur d'affaires",
};

const DOC_FIELDS = [
  { key: 'carte_identite_recto', label: 'CNI Recto', hint: "Recto de votre carte d'identité ou passeport", accept: 'image/*,application/pdf' },
  { key: 'carte_identite_verso', label: 'CNI Verso', hint: "Verso de votre carte d'identité", accept: 'image/*,application/pdf' },
  { key: 'carte_vitale', label: 'Carte vitale', hint: 'Photo de votre carte vitale', accept: 'image/*,application/pdf', secu_only: true },
  { key: 'rib', label: "RIB (relevé d'identité bancaire)", hint: 'Document pour recevoir vos paiements', accept: 'image/*,application/pdf' },
  { key: 'photo_accreditation', label: "Photo d'identité", hint: "Photo fond blanc pour votre carte d'accréditation", accept: 'image/*' },
  { key: 'permis_conduire', label: 'Permis de conduire', hint: 'Recto de votre permis de conduire', accept: 'image/*,application/pdf' },
  { key: 'attestation_secu', label: 'Attestation de sécurité sociale', hint: 'Attestation CPAM de votre numéro de sécurité sociale', accept: 'image/*,application/pdf', secu_only: true },
  { key: 'mutuelle', label: 'Attestation mutuelle', hint: 'Justificatif de votre mutuelle santé (optionnel)', accept: 'image/*,application/pdf' },
  { key: 'extrait_kbis', label: 'Extrait KBIS', hint: "Extrait d'immatriculation de votre micro-entreprise (moins de 3 mois)", accept: 'image/*,application/pdf', ae_only: true },
  { key: 'rc_pro_document', label: 'Attestation Assurance RC Pro', hint: 'Document justifiant votre couverture en Responsabilité Civile Professionnelle', accept: 'image/*,application/pdf', ae_only: true },
];

const MAX_DOC_MB = 10;

// Réduit une photo à 2000 px max côté navigateur : envoi beaucoup plus rapide en 4G,
// lisibilité intacte, et surtout plus de saturation mémoire sur téléphone.
const compressImage = (file) => new Promise((resolve) => {
  if (!file.type?.startsWith('image/') || file.type === 'image/heic' || file.type === 'image/heif') {
    resolve(file);
    return;
  }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const max = 2000;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    if (scale === 1 && file.size <= 1.5 * 1024 * 1024) {
      URL.revokeObjectURL(url);
      resolve(file);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(url);
      if (!blob || blob.size >= file.size) { resolve(file); return; }
      resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.82);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

// Lit le fichier en mémoire : un fichier « en ligne » (Drive, iCloud, Galerie cloud)
// peut être illisible ou vide côté téléphone → on le détecte avant l'envoi.
const readLocalCopy = async (file) => {
  let buf;
  try {
    buf = await file.arrayBuffer();
  } catch {
    throw new Error("Votre téléphone n'a pas pu lire ce fichier (il est peut-être stocké en ligne). Prenez directement une photo, ou enregistrez d'abord le fichier sur le téléphone.");
  }
  if (!buf.byteLength) {
    throw new Error("Ce fichier est vide ou n'a pas pu être lu par votre téléphone. Prenez directement une photo, ou enregistrez d'abord le fichier sur le téléphone.");
  }
  return new File([buf], file.name || 'document', { type: file.type || '' });
};

// Envoi d'un seul document (requête légère, avec progression)
const uploadDocument = (token, docKey, file, onProgress) => new Promise((resolve, reject) => {
  const fd = new FormData();
  fd.append('doc_key', docKey);
  fd.append('file', file, file.name || 'document');
  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${API}/api/onboarding/completion/${token}/document`);
  xhr.upload.onprogress = (e) => {
    if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
  };
  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) { resolve(JSON.parse(xhr.responseText)); return; }
    let msg = "Envoi impossible, réessayez.";
    try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* réponse non JSON */ }
    if (typeof msg !== 'string') msg = "Envoi impossible, choisissez à nouveau le document puis réessayez.";
    const err = new Error(msg);
    err.retryable = xhr.status >= 500;
    reject(err);
  };
  xhr.onerror = () => { const err = new Error('Connexion interrompue, réessayez.'); err.retryable = true; reject(err); };
  xhr.send(fd);
});

// ── Step Indicator ────────────────────────────────────────────────────────────
function StepIndicator({ current, total }) {
  const steps = [
    { label: 'Informations' },
    { label: 'Documents' },
    ...(total >= 3 ? [{ label: 'Octopus' }] : []),
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-y-2 gap-x-1 mb-8">
      {steps.map((s, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={i} className="flex items-center gap-1">
            <div className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-all
              ${active ? 'bg-blue-600 text-white shadow-sm shadow-blue-200' : done ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
              {done ? <CheckCircle size={12} /> : <span className="w-4 h-4 rounded-full flex items-center justify-center text-[10px]">{idx}</span>}
              {s.label}
            </div>
            {i < steps.length - 1 && (
              <div className={`h-px w-4 sm:w-6 ${idx < current ? 'bg-emerald-300' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Field component ───────────────────────────────────────────────────────────
function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        {hint && <span className="text-xs text-gray-400 ml-1.5 font-normal">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

// text-base sur mobile : en dessous de 16px, iOS zoome automatiquement au focus.
const inputCls = "w-full min-w-0 max-w-full box-border border border-gray-200 rounded-xl px-3 sm:px-4 py-3 text-base sm:text-sm bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 outline-none";

export default function CompletionForm() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading');
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    civilite: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    date_naissance: '',
    lieu_naissance: '',
    departement_naissance: '',
    nationalite: 'Française',
    numero_secu: '',
    iban: '',
    contact_urgence_nom: '',
    contact_urgence_tel: '',
    contact_urgence_lien: '',
    partenaires: [],
  });

  // { [key]: { name, status: 'uploading'|'done'|'error', progress, error, file } }
  const [docStates, setDocStates] = useState({});
  const uploading = Object.values(docStates).some(d => d.status === 'uploading');
  const failedDocs = Object.entries(docStates).filter(([, d]) => d.status === 'error');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/onboarding/completion/${token}`);
        if (res.status === 410) { setStatus('expired'); return; }
        if (!res.ok) { setStatus('error'); setError("Lien invalide ou expiré."); return; }
        const data = await res.json();
        setEmployee(data);
        setForm(f => ({
          ...f,
          civilite: data.civilite === 'Monsieur' || data.civilite === 'Madame' ? data.civilite : '',
          telephone: data.telephone || '',
          adresse: data.adresse || '',
          code_postal: data.code_postal || '',
          ville: data.ville || '',
          partenaires: data.partenaires?.length ? data.partenaires : f.partenaires,
        }));
        setStatus('ready');
      } catch {
        setStatus('error');
        setError("Impossible de charger le formulaire. Vérifiez votre connexion.");
      }
    };
    load();
  }, [token]);

  const sendDocument = async (key, file) => {
    setDocStates(prev => ({ ...prev, [key]: { name: file.name, status: 'uploading', progress: 0, file } }));
    try {
      const prepared = await readLocalCopy(await compressImage(file));
      if (prepared.size > MAX_DOC_MB * 1024 * 1024) {
        throw new Error(`Ce fichier fait ${(prepared.size / 1024 / 1024).toFixed(1)} Mo (maximum ${MAX_DOC_MB} Mo). Reprenez la photo.`);
      }
      const onProgress = (p) => setDocStates(prev => ({ ...prev, [key]: { ...prev[key], progress: p } }));
      try {
        await uploadDocument(token, key, prepared, onProgress);
      } catch (e) {
        if (!e.retryable) throw e;
        await new Promise(r => setTimeout(r, 1500)); // 2e essai automatique (réseau mobile instable)
        await uploadDocument(token, key, prepared, onProgress);
      }
      setDocStates(prev => ({ ...prev, [key]: { name: file.name, status: 'done', progress: 100 } }));
    } catch (err) {
      setDocStates(prev => ({
        ...prev,
        [key]: { name: file.name, status: 'error', progress: 0, error: err.message, file },
      }));
    }
  };

  const handleFileChange = (key) => (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // permet de re-choisir le même fichier après une erreur
    if (file) sendDocument(key, file);
  };

  const removeFile = (key) => {
    setDocStates(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const secuDigitCount = (form.numero_secu || '').replace(/\s+/g, '').length;
  // AE et Externe facturent leurs prestations : pas de DPAE, donc ni n° de sécu,
  // ni carte vitale, ni attestation CPAM. Les justificatifs pro (KBIS, RC Pro)
  // ne concernent que les AE.
  const statutUpper = String(employee?.statut_contrat || '').trim().toUpperCase();
  const secuRequired = !['AE', 'EXTERNE'].includes(statutUpper);

  // ── Validation étape 1 ────────────────────────────────────────────────────
  const validateStep1 = () => {
    if (!form.civilite) return "Merci de sélectionner votre civilité (Monsieur / Madame).";
    if (!form.date_naissance) return "La date de naissance est requise.";
    if (!form.partenaires.length) return "Indiquez au moins un partenaire pour lequel vous allez prospecter.";
    if (!form.adresse || !form.code_postal || !form.ville) return "L'adresse complète est requise.";
    if (secuRequired && !isValidNirStructure(form.numero_secu)) return "Le numéro de sécurité sociale doit contenir exactement 15 chiffres.";
    return null;
  };

  const goToStep2 = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError('');
    setStep(2);
    window.scrollTo(0, 0);
  };

  const goBack = () => {
    setError('');
    setStep(s => Math.max(1, s - 1));
    window.scrollTo(0, 0);
  };

  // ── Soumission finale (étape 2) ───────────────────────────────────────────
  // Les documents ont déjà été envoyés un par un : ici on ne transmet que le texte,
  // la requête reste donc légère (quelques Ko) même avec 10 pièces jointes.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) { setError("Un document est en cours d'envoi, patientez quelques secondes."); return; }
    if (failedDocs.length > 0) {
      setError("Un document n'a pas pu être envoyé. Appuyez sur « Réessayer » ou retirez-le avant de valider.");
      return;
    }
    setError('');
    setStatus('submitting');

    try {
      const res = await fetch(`${API}/api/onboarding/completion/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, documents: {}, base_url: window.location.origin }),
      });

      if (!res.ok) {
        let detail = "Une erreur est survenue. Réessayez dans un instant.";
        try { detail = (await res.json()).detail || detail; } catch { /* réponse non JSON */ }
        setError(detail);
        setStatus('ready');
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (data.needs_prerequis) {
        setStatus('ready');
        setStep(3);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setStatus('success');
    } catch {
      setError("Erreur réseau. Réessayez.");
      setStatus('ready');
    }
  };

  // ── Écrans d'état ─────────────────────────────────────────────────────────
  if (status === 'loading') return (
    <div className="force-light min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="animate-spin text-blue-600" size={32} />
    </div>
  );

  if (status === 'expired' || (status === 'error' && !employee)) return (
    <div className="force-light min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <img src="/o-prime-logo.svg" alt="O Prime Energy" className="h-10 mx-auto mb-5" onError={(e) => { e.target.style.display='none'; }} />
        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="text-red-500" size={28} />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide ou expiré</h1>
        <p className="text-gray-500 text-sm">Ce lien de complétion n'est plus valide. Contactez votre responsable pour en obtenir un nouveau.</p>
      </div>
    </div>
  );

  if (status === 'success') return (
    <div className="force-light min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <img src="/o-prime-logo.svg" alt="O Prime Energy" className="h-10 mx-auto mb-5" onError={(e) => { e.target.style.display='none'; }} />
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="text-emerald-500" size={28} />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Dossier envoyé !</h1>
        <p className="text-gray-600 text-sm mb-4">
          Merci <strong>{employee?.prenom} {employee?.nom}</strong>.<br />
          Votre dossier a été transmis. Notre équipe va le valider et vous enverrons un <strong>lien par email pour signer vos documents</strong> dans les prochains jours.
        </p>
      </div>
    </div>
  );

  // ── Formulaire principal ──────────────────────────────────────────────────
  return (
    <div className="force-light min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="text-center mb-6">
          <img src="/o-prime-logo.svg" alt="O Prime Energy" className="h-14 mx-auto mb-4" onError={(e) => { e.target.style.display='none'; }} />
          <h1 className="text-2xl font-bold text-gray-900">Compléter votre dossier</h1>
          <p className="text-gray-500 text-sm mt-1">
            Bonjour <strong>{employee?.prenom} {employee?.nom}</strong> —{' '}
            {STATUT_LABELS[employee?.statut_contrat] || employee?.statut_contrat}
            {employee?.agence && ` · ${employee.agence}`}
          </p>
        </div>

        <StepIndicator current={step} total={form.partenaires.includes('octopus') ? 3 : 2} />

        {/* ── ÉTAPE 1 : Informations personnelles ───────────────────────── */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Coordonnées */}
            <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Coordonnées</h2>
              <Field label="Vous allez prospecter pour" required>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'octopus', label: 'Octopus Energy' },
                    { value: 'ilek', label: 'ilek' },
                    { value: 'bouygues', label: 'Bouygues Telecom' },
                  ].map(p => {
                    const on = form.partenaires.includes(p.value);
                    return (
                      <button key={p.value} type="button"
                        onClick={() => setForm(f => ({
                          ...f,
                          partenaires: on
                            ? f.partenaires.filter(x => x !== p.value)
                            : [...f.partenaires, p.value],
                        }))}
                        data-testid={`partenaire-${p.value}`}
                        className={`px-3 py-2 rounded-xl text-sm border-2 transition-colors duration-150 ${
                          on ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}>
                        {on ? '✓ ' : ''}{p.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Téléphone">
                <input type="tel" value={form.telephone} onChange={set('telephone')} placeholder="06 12 34 56 78" className={inputCls} />
              </Field>
              <Field label="Adresse" required>
                <input type="text" value={form.adresse} onChange={set('adresse')} placeholder="12 rue des Fleurs" required className={inputCls} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-3">
                <Field label="Code postal" required>
                  <input type="text" value={form.code_postal} onChange={set('code_postal')} placeholder="69001" required maxLength={10} className={inputCls} />
                </Field>
                <Field label="Ville" required>
                  <input type="text" value={form.ville} onChange={set('ville')} placeholder="Lyon" required className={inputCls} />
                </Field>
              </div>
            </div>

            {/* Identité */}
            <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Identité</h2>
              <Field label="Civilité" required>
                <select value={form.civilite} onChange={set('civilite')} className={`${inputCls} bg-white`} data-testid="completion-civilite-select">
                  <option value="">— Sélectionner —</option>
                  <option value="Monsieur">Monsieur</option>
                  <option value="Madame">Madame</option>
                </select>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-3">
                <Field label="Date de naissance" required>
                  <input type="date" value={form.date_naissance} onChange={set('date_naissance')} required className={inputCls} />
                </Field>
                <Field label="Lieu de naissance">
                  <input type="text" value={form.lieu_naissance} onChange={set('lieu_naissance')} placeholder="Lyon" className={inputCls} />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-3">
                <Field label="Département de naissance">
                  <input type="text" value={form.departement_naissance} onChange={set('departement_naissance')} placeholder="69" className={inputCls} />
                </Field>
                <Field label="Nationalité">
                  <input type="text" value={form.nationalite} onChange={set('nationalite')} placeholder="Française" className={inputCls} />
                </Field>
              </div>
            </div>

            {/* Données administratives */}
            <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Données administratives</h2>
              {secuRequired && (
                <Field label="N° de sécurité sociale" hint="(15 chiffres, chiffré, non visible par l'équipe)" required>
                  <input type="text" value={form.numero_secu} onChange={set('numero_secu')} placeholder="1 85 12 69 123 456 78" maxLength={21} className={`${inputCls} font-mono`} data-testid="completion-numero-secu-input" />
                  <p className={`text-xs mt-1 ${secuDigitCount === 15 ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {secuDigitCount}/15 chiffres
                  </p>
                </Field>
              )}
              <Field label="IBAN">
                <input type="text" value={form.iban} onChange={set('iban')} placeholder="FR76 1234 5678 9012 3456 7890 123" className={`${inputCls} font-mono`} />
              </Field>
            </div>

            {/* Contact d'urgence */}
            <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
              <div>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Contact d'urgence</h2>
                <p className="text-xs text-gray-400 mt-1">Personne à contacter en cas d'urgence.</p>
              </div>
              <Field label="Nom et prénom">
                <input type="text" value={form.contact_urgence_nom} onChange={set('contact_urgence_nom')} placeholder="Marie Dupont" className={inputCls} />
              </Field>
              <Field label="Téléphone">
                <input type="tel" value={form.contact_urgence_tel} onChange={set('contact_urgence_tel')} placeholder="06 12 34 56 78" className={inputCls} />
              </Field>
              <Field label="Lien de parenté">
                <select value={form.contact_urgence_lien} onChange={set('contact_urgence_lien')} className={`${inputCls} bg-white`}>
                  <option value="">— Sélectionner —</option>
                  <option>Conjoint(e)</option>
                  <option>Parent</option>
                  <option>Enfant</option>
                  <option>Frère / Sœur</option>
                  <option>Ami(e)</option>
                  <option>Autre</option>
                </select>
              </Field>
            </div>

            {error && <ErrorBanner message={error} />}

            <button
              type="button"
              onClick={goToStep2}
              data-testid="step1-next-btn"
              className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 transition shadow-lg shadow-blue-200"
            >
              Documents justificatifs <ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* ── ÉTAPE 2 : Documents ───────────────────────────────────────── */}
        {/* ── ÉTAPE 3 : Prérequis Octopus (non bloquante) ───────────────── */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle size={18} />
                <p className="font-semibold text-sm">Dossier et documents bien reçus !</p>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Avant de démarrer sur le terrain, il reste 2 étapes à valider auprès d'Octopus.
              </p>
            </div>
            <OctopusPrerequisSteps token={token} />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-4">
              <div>
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Documents justificatifs</h2>
                <p className="text-xs text-gray-400 mt-1">Photos ou PDF. Envoyés directement dans votre dossier sécurisé.</p>
              </div>
              {statutUpper === 'AE' && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-xs text-green-800">
                  <span className="shrink-0 font-bold bg-green-200 text-green-800 px-1.5 py-0.5 rounded">AE</span>
                  Deux documents supplémentaires vous sont demandés : l'extrait KBIS et l'attestation d'assurance RC Pro.
                </div>
              )}
              <div className="space-y-3">
                {DOC_FIELDS
                  .filter(f => (!f.ae_only || statutUpper === 'AE') && (!f.secu_only || secuRequired))
                  .map(({ key, label, hint, accept, ae_only }) => {
                  const st = docStates[key];
                  return (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                      {label}
                      {ae_only && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 rounded">AE</span>}
                    </label>
                    {st?.status === 'uploading' ? (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl" data-testid={`doc-uploading-${key}`}>
                        <div className="flex items-center gap-2">
                          <Loader2 size={16} className="text-blue-600 shrink-0 animate-spin" />
                          <span className="text-sm text-blue-800 flex-1 truncate">{st.name}</span>
                          <span className="text-xs text-blue-700 font-semibold">{st.progress}%</span>
                        </div>
                        <div className="mt-2 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 transition-all duration-200" style={{ width: `${st.progress}%` }} />
                        </div>
                      </div>
                    ) : st?.status === 'done' ? (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid={`doc-done-${key}`}>
                        <CheckCircle size={16} className="text-emerald-600 shrink-0" />
                        <span className="text-sm text-emerald-800 flex-1 truncate">{st.name}</span>
                        <span className="text-[10px] font-semibold text-emerald-700 uppercase">reçu</span>
                        <button type="button" onClick={() => removeFile(key)} className="p-1 text-gray-400 hover:text-red-500 transition" data-testid={`doc-remove-${key}`}>
                          <X size={14} />
                        </button>
                      </div>
                    ) : st?.status === 'error' ? (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-2" data-testid={`doc-error-${key}`}>
                        <div className="flex items-center gap-2">
                          <AlertCircle size={16} className="text-red-500 shrink-0" />
                          <span className="text-sm text-red-800 flex-1 truncate">{st.name}</span>
                          <button type="button" onClick={() => removeFile(key)} className="p-1 text-gray-400 hover:text-red-500 transition">
                            <X size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-red-700">{st.error}</p>
                        <button type="button" onClick={() => sendDocument(key, st.file)}
                          className="text-xs font-semibold text-white bg-red-500 px-3 py-1.5 rounded-lg hover:bg-red-600 transition"
                          data-testid={`doc-retry-${key}`}>
                          Réessayer
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-start gap-2 p-3 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition">
                        <Upload size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-500">{hint}</span>
                        <input type="file" accept={accept} onChange={handleFileChange(key)} className="hidden" />
                      </label>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>

            {error && <ErrorBanner message={error} />}

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button type="button" onClick={goBack} className="flex items-center justify-center gap-1 px-5 py-4 rounded-2xl bg-white border border-gray-200 text-gray-600 font-medium text-sm hover:bg-gray-50 transition">
                <ChevronLeft size={16} /> Retour
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={status === 'submitting' || uploading}
                data-testid="step2-submit-btn"
                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 transition shadow-lg shadow-blue-200 disabled:opacity-60"
              >
                {status === 'submitting'
                  ? <><Loader2 size={18} className="animate-spin" /> Validation…</>
                  : uploading
                    ? <><Loader2 size={18} className="animate-spin" /> Envoi des documents…</>
                    : <><ChevronRight size={18} /> Envoyer mon dossier</>
                }
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ErrorBanner({ message }) {
  return (
    <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
      {message}
    </div>
  );
}
